import API, { baseURL } from './api';
import { getSucursalInfo, getUserInfo } from './auth';

// Ported from sge_infantil_web/src/services/student.js + the endpoints used
// directly inside src/pages/student/{create,edit,mantain,inativate}/index.js
// (those pages call api.* inline instead of going through services/student.js
// for create/registration/print/notify — same shape kept here for parity).

export async function listActiveStudents(filterYear, sucursalId) {
  const year = filterYear || new Date().getFullYear();
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/registration/current/registration/${id}?filterYear=${year}`);
}

// Direct fetch of a single registration (with its nested student) — used as
// a fallback when the edit page is opened without router state (e.g. a
// direct URL visit rather than navigating from the list).
export async function getRegistrationById(registrationId) {
  return API.get(`${baseURL}api/registration/${registrationId}`);
}

export async function findFrequencies(sucursalId) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/frequency/${id}`);
}
export const listFrequencies = findFrequencies;

export async function listExtraActivities() {
  const sucursalId = getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/extraconfig/${sucursalId}`);
}

export async function uploadStudentPhoto(file) {
  const formData = new FormData();
  formData.append('file', file);
  // Let axios/the browser set Content-Type — a manual 'multipart/form-data'
  // header strips the required boundary param and the server can't parse the body.
  return API.post(`${baseURL}api/file-upload/upload`, formData);
}
// Alias used by the create wizard, named for symmetry with uploadStudentPhotoEdit.
export const uploadStudentPhotoCreate = uploadStudentPhoto;

// Edit uses a *different* upload endpoint from create — verified directly in
// sge_infantil_web/src/pages/student/edit/index.js (line 610, /api/upload/pictures)
// vs create/index.js (line 659, /api/file-upload/upload). Real inconsistency
// in the original app, kept intentionally.
export async function uploadStudentPhotoEdit(file) {
  const formData = new FormData();
  formData.append('file', file);
  return API.post(`${baseURL}api/upload/pictures`, formData);
}

export async function createStudent(payload) {
  return API.post(`${baseURL}api/student`, payload);
}

export async function createRegistration(payload) {
  return API.post(`${baseURL}api/registration/registration`, payload);
}

export async function updateStudent(id, payload) {
  return API.put(`${baseURL}api/student/edit/${id}`, payload);
}

export async function updateRegistration(id, payload) {
  return API.put(`${baseURL}api/registration/${id}`, payload);
}

// "Inactivar" — asks for a reason, matching the original's inativate flow.
export async function inactivateRegistration(registrationId, reason, inactivatedBy) {
  return API.put(`${baseURL}api/registration/inactivate/${registrationId}`, {
    reason,
    inactivatedBy,
  });
}

export async function listInactiveRegistrations(year, sucursalId) {
  const id = sucursalId || getSucursalInfo()?.id || 1;
  const params = year ? { year } : {};
  return API.get(`${baseURL}api/registration/inactive/${id}`, { params });
}

export async function reactivateRegistration(registrationId, reactivatedBy) {
  return API.put(`${baseURL}api/registration/reactivate/${registrationId}`, { reactivatedBy });
}

// "Remover" — the original's delete path, same underlying endpoint as the
// (differently-named) student inactivate-by-id call, distinguished by payload.
export async function removeStudent(studentId, { registrationId, payments, activatedBy } = {}) {
  return API.put(`${baseURL}api/student/inativate/student/${studentId}`, {
    payments: payments || [],
    registrationId,
    activatedBy: activatedBy || getUserInfo()?.id,
  });
}

export async function notifyStudents(payload) {
  return API.post(`${baseURL}api/notification`, payload);
}
export const sendNotification = notifyStudents;

export async function getEnrollmentReport(registrationId) {
  return API.get(`${baseURL}api/report/student/${registrationId}`);
}

export async function getRenewalReport(studentId, sucursalId) {
  return API.get(`${baseURL}api/report/renewal/report/${studentId}?sucursalId=${sucursalId}`);
}

export async function getProfileReport(registrationId) {
  return API.get(`${baseURL}api/report/registration/${registrationId}`);
}
export const printFicha = getProfileReport;

// Comprovativo de Cadastro: new enrolments use the "student" report, renewals
// use the "renewal" report — matches handleDownload in mantain/index.js.
export async function printComprovativo({ registrationId, studentId, isRenewal, sucursalId }) {
  if (isRenewal) {
    const id = sucursalId || getSucursalInfo()?.id || 1;
    return getRenewalReport(studentId, id);
  }
  return getEnrollmentReport(registrationId);
}

// Renewal candidates: registrations from a past academic year (the
// student's last known enrollment) that haven't been carried over to the
// current one yet. Verified against sge_infantil_web's
// findUnrenewedStudents (src/pages/student/renew/index.js) — it hits the
// exact same "current registration" route listActiveStudents uses, just
// defaulting filterYear to last year instead of the current one; there is
// no separate "/unrenewd" endpoint.
export async function listUnrenewedStudents(filterYear) {
  const year = filterYear || new Date().getFullYear() - 1;
  const sucursalId = getSucursalInfo()?.id || 1;
  return API.get(`${baseURL}api/registration/current/registration/${sucursalId}?filterYear=${year}`);
}
